import React, { useState, useEffect, createRef, useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import api from "../../Api";
import { AuthContext } from "../../context/authContext";

const Verify = () => {
  const { refreshUser } = useContext(AuthContext);
  const location = useLocation();
  const token = location.pathname.split("/")[4];
  const navigate = useNavigate();
  
  // Check if this is a password reset flow
  const searchParams = new URLSearchParams(location.search);
  const isPasswordReset = searchParams.get('flow') === 'reset';

  // Override body background for this page
  useEffect(() => {
    const originalBackground = document.body.style.background;
    document.body.style.background = 'linear-gradient(135deg, #f5f7fa 0%, #e4e8ec 100%)';
    return () => {
      document.body.style.background = originalBackground;
    };
  }, []);

  const [code, setCode] = useState(Array(6).fill(""));
  const inputsRef = Array(6).fill().map(() => createRef());
  const [formErrors, setFormErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [resendSeconds, setResendSeconds] = useState(30);
  const [isResendDisabled, setIsResendDisabled] = useState(true);
  const [resendSuccess, setResendSuccess] = useState(false);

  // Countdown timer for resend
  useEffect(() => {
    let timer;
    if (isResendDisabled && resendSeconds > 0) {
      timer = setInterval(() => {
        setResendSeconds((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            setIsResendDisabled(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [isResendDisabled, resendSeconds]);

  const handleChange = (index) => (e) => {
    const value = e.target.value;
    
    // Only allow numbers
    if (value && !/^\d$/.test(value)) return;
    
    let newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    // Clear error when typing
    if (formErrors.code) {
      setFormErrors({});
    }

    // Auto-focus next input
    if (value && inputsRef[index + 1]) {
      inputsRef[index + 1].current.focus();
    }
  };

  const handleKeyDown = (index) => (e) => {
    // Handle backspace to go to previous input
    if (e.key === "Backspace" && !code[index] && inputsRef[index - 1]) {
      inputsRef[index - 1].current.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").slice(0, 6);
    if (/^\d+$/.test(pastedData)) {
      const newCode = pastedData.split("").concat(Array(6).fill("")).slice(0, 6);
      setCode(newCode);
      // Focus the last filled input or the next empty one
      const focusIndex = Math.min(pastedData.length, 5);
      inputsRef[focusIndex].current.focus();
    }
  };

  const validateForm = () => {
    const errors = {};
    const isCodeValid = code.every((el) => el !== "");

    if (!isCodeValid) {
      errors.code = "Please enter all 6 digits";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    
    const isValid = validateForm();

    if (isValid) {
      setIsLoading(true);
      try {
        await api.post(`/classBooking/api/auth/verify?token=${token}`, {
          code: code.join(""),
        });
        
        // Refresh user context (sets login state)
        refreshUser();
        
        if (isPasswordReset) {
          // For SMS login (forgot password), go to home page
          navigate(`/en/`);
        } else {
          // For signup, go to success page
          navigate(`/en/signup/successful`);
        }
      } catch (err) {
        setFormErrors(err.response?.data || { code: "Invalid code. Please try again." });
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleResendSMS = async () => {
    if (isResendDisabled) return;
    
    try {
      await api.post(`/classBooking/api/auth/resendSMS?token=${token}`);
      setIsResendDisabled(true);
      setResendSeconds(30);
      setResendSuccess(true);
      setTimeout(() => setResendSuccess(false), 3000);
    } catch (err) {
      setFormErrors(err.response?.data || { general: "Failed to resend code." });
    }
  };

  return (
    <PageContainer>
      <VerifyCard>
        <BackButton onClick={() => navigate(-1)}>
          ← Back
        </BackButton>

        <HeaderSection>
          <IconWrapper>📱</IconWrapper>
          <TitleText>Verify Your Mobile</TitleText>
          <SubText>Enter the 6-digit code sent to your mobile via SMS</SubText>
        </HeaderSection>

        <FormSection onSubmit={handleSubmit}>
          <CodeInputContainer onPaste={handlePaste}>
            {code.map((digit, index) => (
              <CodeInput
                key={index}
                type="text"
                inputMode="numeric"
                value={digit}
                onChange={handleChange(index)}
                onKeyDown={handleKeyDown(index)}
                ref={inputsRef[index]}
                maxLength={1}
                $hasError={formErrors.code}
                $isFilled={digit !== ""}
              />
            ))}
          </CodeInputContainer>
          
          {formErrors.code && <ErrorText>{formErrors.code}</ErrorText>}
          {formErrors.general && <ErrorText>{formErrors.general}</ErrorText>}
          {resendSuccess && <SuccessText>Code sent successfully!</SuccessText>}

          <SubmitButton type="submit" disabled={isLoading}>
            {isLoading ? <Spinner /> : "Verify Code"}
          </SubmitButton>

          <ResendSection>
            <ResendText>Didn't receive the code?</ResendText>
            <ResendButton 
              type="button"
              onClick={handleResendSMS} 
              disabled={isResendDisabled}
            >
              {isResendDisabled ? (
                <>Resend in <CountdownBadge>{resendSeconds}s</CountdownBadge></>
              ) : (
                "Resend Code"
              )}
            </ResendButton>
          </ResendSection>
        </FormSection>
      </VerifyCard>

      <PoweredByFooter>
        <div>
          Powered by <img src="/dynamic_logo.svg" alt="DMG Logo" /> | booking.dynevents.com
        </div>
      </PoweredByFooter>
    </PageContainer>
  );
};

export default Verify;

// Keyframes
const shake = keyframes`
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-5px); }
  75% { transform: translateX(5px); }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
`;

// Styled Components
const PageContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
  padding-bottom: 60px;
`;

const VerifyCard = styled.div`
  width: 100%;
  max-width: 420px;
  background: white;
  border-radius: 24px;
  padding: 40px 32px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);
  position: relative;
`;

const BackButton = styled.button`
  position: absolute;
  top: 20px;
  left: 20px;
  background: none;
  border: none;
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  color: #666;
  cursor: pointer;
  padding: 8px 12px;
  border-radius: 8px;
  transition: all 0.2s ease;

  &:hover {
    background: #f5f5f5;
    color: #333;
  }
`;

const HeaderSection = styled.div`
  text-align: center;
  margin-bottom: 32px;
  margin-top: 20px;
`;

const IconWrapper = styled.div`
  font-size: 48px;
  margin-bottom: 16px;
  animation: ${pulse} 2s ease-in-out infinite;
`;

const TitleText = styled.h1`
  font-family: 'Poppins', sans-serif;
  font-size: 24px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 8px 0;
`;

const SubText = styled.p`
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  color: #888;
  margin: 0;
  line-height: 1.5;
`;

const FormSection = styled.form`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
`;

const CodeInputContainer = styled.div`
  display: flex;
  gap: 10px;
  justify-content: center;
`;

const CodeInput = styled.input`
  width: 48px;
  height: 56px;
  border: 2px solid ${props => props.$hasError ? '#ff6b6b' : props.$isFilled ? '#1a73e8' : '#e8e8e8'};
  border-radius: 12px;
  font-family: 'Poppins', sans-serif;
  font-size: 24px;
  font-weight: 600;
  text-align: center;
  color: #1a1a1a;
  background: ${props => props.$hasError ? '#fff5f5' : props.$isFilled ? '#f0f7ff' : '#f8f9fa'};
  outline: none;
  transition: all 0.2s ease;
  animation: ${props => props.$hasError ? shake : 'none'} 0.4s ease;

  &:focus {
    border-color: ${props => props.$hasError ? '#ff6b6b' : '#1a73e8'};
    background: white;
    box-shadow: 0 0 0 4px ${props => props.$hasError ? 'rgba(255, 107, 107, 0.1)' : 'rgba(26, 115, 232, 0.1)'};
  }

  &::placeholder {
    color: #ccc;
  }
`;

const ErrorText = styled.span`
  font-family: 'Poppins', sans-serif;
  font-size: 13px;
  color: #ff6b6b;
  text-align: center;
`;

const SuccessText = styled.span`
  font-family: 'Poppins', sans-serif;
  font-size: 13px;
  color: #34a853;
  text-align: center;
  padding: 8px 16px;
  background: #e8f5e9;
  border-radius: 8px;
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 16px;
  background: #1a1a1a;
  color: white;
  border: none;
  border-radius: 14px;
  font-family: 'Poppins', sans-serif;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover:not(:disabled) {
    background: #333;
    transform: translateY(-1px);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const Spinner = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const ResendSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
`;

const ResendText = styled.span`
  font-family: 'Poppins', sans-serif;
  font-size: 13px;
  color: #888;
`;

const ResendButton = styled.button`
  background: none;
  border: none;
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: ${props => props.disabled ? '#aaa' : '#1a73e8'};
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  padding: 8px 16px;
  border-radius: 8px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 6px;

  &:hover:not(:disabled) {
    background: #f0f7ff;
  }
`;

const CountdownBadge = styled.span`
  background: #e8e8e8;
  color: #666;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 500;
`;

const PoweredByFooter = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  height: 36px;
  font-size: 12px;
  font-family: 'Poppins', sans-serif;
  color: #666;
  background-color: white;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.08);
  
  @media (min-width: 500px) {
    width: 500px;
    left: calc((100vw - 500px) / 2);
    right: calc((100vw - 500px) / 2);
  }

  img {
    display: inline-block;
    width: 20px;
    vertical-align: middle;
    margin: 0 4px;
  }
`;
